import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./ServiceMonitoring.module.css";

const normalizeStatus = (plan) =>
  String(plan?.status ?? "").toLowerCase().trim();

const isCancelAtPeriodEnd = (plan) => {
  const v = plan?.cancelAtPeriodEnd;
  return v === true || v === "true" || v === 1;
};

const isOfflinePlan = (plan) => {
  const s = normalizeStatus(plan);
  return (
    s === "inactive" ||
    s === "canceled" ||
    s === "cancelled" ||
    s === "expired"
  );
};

const isPlanEndingSoon = (plan) => {
  if (!plan || isOfflinePlan(plan)) return false;
  const st = normalizeStatus(plan);
  if (st === "canceling") return true;
  if (isCancelAtPeriodEnd(plan)) return true;
  const rd = plan.renewalDate ? new Date(plan.renewalDate) : null;
  const renewalFuture =
    rd && !Number.isNaN(rd.getTime()) && rd > new Date();
  if (st === "failed" && renewalFuture && plan.autoRenew === false) {
    return true;
  }
  if (st === "active" && renewalFuture && plan.autoRenew === false) {
    return true;
  }
  return false;
};

const CircleProgress = ({
  percentage,
  label,
  count,
  color,
  onClick,
  href,
  hint,
  hintClassName,
  labelClassName,
  countClassName,
}) => {
  const radius = 30;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  const circleContent = (
    <div
      className={styles.circleContainer}
      onClick={onClick}
      style={{ cursor: onClick || count >= 1 ? "pointer" : "default" }}
    >
      <svg
        className={styles.circleProgress}
        width="80"
        height="80"
        viewBox="0 0 80 80"
      >
        <circle
          className={styles.circleBackground}
          cx="40"
          cy="40"
          r={radius}
          strokeWidth="4"
        />
        <circle
          className={styles.circleValue}
          cx="40"
          cy="40"
          r={radius}
          strokeWidth="4"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          style={{ stroke: color }}
        />
      </svg>
      <div className={styles.circleContent}>
        <span
          className={`${styles.circleCount} ${countClassName || ""}`.trim()}
        >
          {count}
        </span>
      </div>
      <div className={styles.circleText}>
        <span
          className={`${styles.circleLabel} ${labelClassName || ""}`.trim()}
        >
          {label}
        </span>
        {hint ? (
          <span className={hintClassName || styles.circleHint}>{hint}</span>
        ) : null}
      </div>
    </div>
  );

  if (href && count > 0) {
    return (
      <Link to={href} style={{ textDecoration: "none", color: "inherit" }}>
        {circleContent}
      </Link>
    );
  }

  return circleContent;
};

const ServiceMonitoring = ({ plans = [], domains = [] }) => {
  const { t } = useTranslation("dashboard");

  const endingSoonWebsites = plans.filter(isPlanEndingSoon).length;

  const goodHealthWebsites = plans.filter(
    (plan) =>
      normalizeStatus(plan) === "active" &&
      !isOfflinePlan(plan) &&
      !isPlanEndingSoon(plan)
  ).length;

  const failedWebsites = plans.filter(
    (plan) =>
      normalizeStatus(plan) === "failed" && !isPlanEndingSoon(plan)
  ).length;

  const offlineWebsites = plans.filter((plan) => {
    if (isPlanEndingSoon(plan)) return false;
    return isOfflinePlan(plan);
  }).length;

  const totalWebsites =
    goodHealthWebsites +
    endingSoonWebsites +
    failedWebsites +
    offlineWebsites;

  const showEndingSoonInfo = endingSoonWebsites > 0;
  const showFailedInfo = !showEndingSoonInfo && failedWebsites > 0;
  const middleWebsiteCount = showEndingSoonInfo
    ? endingSoonWebsites
    : failedWebsites;

  const now = new Date();
  const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

  const goodDomains = domains.filter((domain) => {
    const expDate = new Date(domain.renewalDate);
    return expDate > thirtyDaysFromNow;
  }).length;

  const expiringSoonDomains = domains.filter((domain) => {
    const expDate = new Date(domain.renewalDate);
    return expDate > now && expDate <= thirtyDaysFromNow;
  }).length;

  const expiredDomains = domains.filter((domain) => {
    const expDate = new Date(domain.renewalDate);
    return expDate <= now;
  }).length;

  const totalDomains = goodDomains + expiringSoonDomains + expiredDomains;

  const websiteData = [
    {
      percentage:
        totalWebsites > 0 ? (goodHealthWebsites / totalWebsites) * 100 : 0,
      label: t("serviceMonitoring.online"),
      count: goodHealthWebsites,
      color: "#10b981",
      href: "/products",
    },
    {
      percentage:
        totalWebsites > 0
          ? (middleWebsiteCount / totalWebsites) * 100
          : 0,
      label: t("serviceMonitoring.canceling"),
      count: middleWebsiteCount,
      color: showEndingSoonInfo
        ? "#f97316"
        : showFailedInfo
          ? "#dc2626"
          : "#94a3b8",
      href: "/billing",
      labelClassName: showEndingSoonInfo
        ? styles.circleLabelInfo
        : showFailedInfo
          ? styles.circleLabelFailed
          : styles.circleLabelNeutral,
    },
    {
      percentage:
        totalWebsites > 0 ? (offlineWebsites / totalWebsites) * 100 : 0,
      label: t("serviceMonitoring.offline"),
      count: offlineWebsites,
      color: offlineWebsites > 0 ? "#dc2626" : "#94a3b8",
      href: "/billing",
      labelClassName:
        offlineWebsites > 0 ? styles.circleLabelOfflineAccent : undefined,
      countClassName:
        offlineWebsites > 0 ? styles.circleCountOfflineAccent : undefined,
    },
  ];

  const domainData = [
    {
      percentage: totalDomains > 0 ? (goodDomains / totalDomains) * 100 : 0,
      label: t("serviceMonitoring.active"),
      count: goodDomains,
      color: "#10b981",
      href: "/products",
    },
    {
      percentage:
        totalDomains > 0 ? (expiringSoonDomains / totalDomains) * 100 : 0,
      label: t("serviceMonitoring.expireSoon"),
      count: expiringSoonDomains,
      color: "#f59e0b",
      href: "/billing",
    },
    {
      percentage: totalDomains > 0 ? (expiredDomains / totalDomains) * 100 : 0,
      label: t("serviceMonitoring.expired"),
      count: expiredDomains,
      color: "#ef4444",
      href: "/billing",
    },
  ];

  return (
    <div className={styles.serviceMonitoring}>
      <div className={styles.titleBar}>
        <h3 className={styles.title}>{t("serviceMonitoring.title")}</h3>
        <span className={styles.titleBadge}>
          {t("serviceMonitoring.headerBadge")}
        </span>
      </div>

      <div className={styles.statusGrid}>
        <div className={styles.statusSection}>
          <h4 className={styles.sectionTitle}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 2a14.5 14.5 0 0 0 0 20a14.5 14.5 0 0 0 0-20" />
              <path d="M2 12h20" />
            </svg>
            {t("serviceMonitoring.websites")}
          </h4>
          <div className={styles.circleGrid}>
            {websiteData.map((item, index) => (
              <CircleProgress
                key={`website-${index}`}
                percentage={item.percentage}
                label={item.label}
                count={item.count}
                color={item.color}
                href={item.href}
                hint={item.hint}
                hintClassName={item.hintClassName}
                labelClassName={item.labelClassName}
                countClassName={item.countClassName}
              />
            ))}
          </div>
        </div>

        <div className={styles.statusSection}>
          <h4 className={styles.sectionTitle}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M9 12l2 2 4-4" />
              <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3" />
              <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3" />
              <path d="M12 3c0 1-1 3-3 3s-3-2-3-3 1-3 3-3 3 2 3 3" />
              <path d="M12 21c0-1 1-3 3-3s3 2 3 3-1 3-3 3-3-2-3-3" />
            </svg>
            {t("serviceMonitoring.domains")}
          </h4>
          <div className={styles.circleGrid}>
            {domainData.map((item, index) => (
              <CircleProgress
                key={`domain-${index}`}
                percentage={item.percentage}
                label={item.label}
                count={item.count}
                color={item.color}
                href={item.href}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceMonitoring;
